import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useAppUsersForAdmin } from 'data/hooks/users/useAppUsersForAdmin'
import { fetchAndReturn } from 'data/utils/utils'
import { SelectConnectionInput } from 'features/Agents/configuration/connections/SelectConnectionInput'
import { RerunMessagesModal } from 'features/common/RerunMessagesModal'

import { Box } from 'ui/components/Box'
import { Field } from 'ui/components/Field'
import { Input } from 'ui/components/Input'
import { Select, SelectOption } from 'ui/components/Select'
import { Body } from 'ui/components/Text'

import { TriggerConfigProps } from './types'

export type EmailReceivedTriggerConfigType = {
    connection_id: string
    filters?: {
        from_address?: string
        to_address?: string
        subject_contains?: string
    }
    user_sid: string
}

export function EmailReceivedTriggerConfig({
    agentSid,
    triggerSid,
    config,
    onChange,
}: TriggerConfigProps<EmailReceivedTriggerConfigType>) {
    const { data: users } = useAppUsersForAdmin(true)

    const methods = useForm({
        defaultValues: {
            connection_id: config.connection_id || '',
        },
    })

    const rerunAgentMessages = React.useCallback(
        async (messageIds: string[]) => {
            await fetchAndReturn(`agents/${agentSid}/triggers/rerun-messages/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    trigger_sid: triggerSid,
                    message_ids: messageIds,
                    automation_connection_sid: config.connection_id,
                }),
            })
        },
        [agentSid, triggerSid, config.connection_id]
    )

    // Watch for changes to connection_id and propagate to parent
    React.useEffect(() => {
        const subscription = methods.watch((value) => {
            if (value.connection_id !== config.connection_id) {
                onChange({
                    ...config,
                    connection_id: value.connection_id || '',
                })
            }
        })
        return () => subscription.unsubscribe()
    }, [config, methods, onChange])

    return (
        <Box flex column gap="m">
            <FormProvider {...methods}>
                <SelectConnectionInput
                    name="connection_id"
                    label="Email Connection"
                    integrationId="google-mail"
                    agentSid={agentSid}
                />
            </FormProvider>
            <Field label="Filters (optional)" width="5xl" shrink mt="m">
                <Box display="grid" style={{ gridTemplateColumns: 'auto 1fr' }} gap="m">
                    <Box display="flex" alignItems="center">
                        <Body style={{ verticalAlign: 'middle' }} size="s">
                            From Address
                        </Body>
                    </Box>
                    <Input
                        name="from_address"
                        value={config.filters?.from_address}
                        onChange={(e) =>
                            onChange({
                                ...config,
                                filters: {
                                    ...config.filters,
                                    from_address: e.target.value,
                                },
                            })
                        }
                    />
                    <Box display="flex" alignItems="center">
                        <Body style={{ verticalAlign: 'middle' }} size="s">
                            To Address
                        </Body>
                    </Box>
                    <Input
                        name="to_address"
                        value={config.filters?.to_address}
                        onChange={(e) =>
                            onChange({
                                ...config,
                                filters: {
                                    ...config.filters,
                                    to_address: e.target.value,
                                },
                            })
                        }
                    />
                    <Box display="flex" alignItems="center">
                        <Body style={{ verticalAlign: 'middle' }} size="s">
                            Subject
                        </Body>
                    </Box>
                    <Input
                        name="subject"
                        value={config.filters?.subject_contains}
                        onChange={(e) =>
                            onChange({
                                ...config,
                                filters: {
                                    ...config.filters,
                                    subject_contains: e.target.value,
                                },
                            })
                        }
                    />
                </Box>
            </Field>
            <Field label="Run as User" width="5xl" shrink>
                <Select
                    value={config.user_sid}
                    onChange={(value) =>
                        onChange({
                            ...config,
                            user_sid: value,
                        })
                    }
                >
                    {users?.map((user: UserDto) => (
                        <SelectOption key={user._sid} value={user._sid} label={user.name} />
                    ))}
                </Select>
            </Field>
            {triggerSid && (
                <Field label="Actions" width="5xl" shrink>
                    <RerunMessagesModal rerunFn={rerunAgentMessages} />
                </Field>
            )}
        </Box>
    )
}
